// src/utils/Onboarding/hairgenJobTracker.js

/**
 * HAIRGEN JOB TRACKER
 * 
 * Singleton module that tracks background hair generation jobs.
 * Allows triggering the job early (in UploadSlide) and consuming the result
 * later (in ImageGenSlide) without duplicate API calls.
 * 
 * KEY DESIGN PRINCIPLES:
 * - Only ONE job tracked at a time (latest upload wins)
 * - New uploads automatically invalidate previous jobs
 * - Module-level state persists across React component lifecycles
 * - Session storage provides persistence across hot reloads
 * - Race condition safe via jobId matching
 * 
 * FLOW:
 * 1. UploadSlide calls startHairgenJob() after Firebase upload
 * 2. Job runs in background while user is on ImageAnalysisSlide
 * 3. ImageGenSlide calls getHairgenJobStatus() to check result
 * 4. If pending, await the promise; if complete, use cached result
 * 
 * MULTIPLE UPLOAD HANDLING:
 * - Each new upload gets a unique jobId (timestamp + storage path hash)
 * - Starting a new job invalidates any previous job
 * - ImageGenSlide only accepts results matching current jobId
 */

// ========================================
// MODULE-LEVEL STATE (Singleton)
// ========================================
let currentJob = {
  jobId: null,
  inputPath: null,       // Firebase storage path of input image
  status: 'idle',        // 'idle' | 'pending' | 'success' | 'error'
  result: null,          // Success result from API
  error: null,           // Error message if failed
  startTime: null,       // When job started (ms timestamp)
  endTime: null,         // When job completed (ms timestamp)
  promise: null,         // The pending promise (not persisted)
};

const STORAGE_KEY = 'mm_hairgen_job_v1';

// ========================================
// PERSISTENCE (Session Storage)
// ========================================

/**
 * Load job state from session storage.
 * Note: The promise cannot be restored, only metadata.
 */
function loadFromStorage() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      // Only restore completed jobs (pending jobs are lost on refresh)
      if (data.status === 'success' || data.status === 'error') {
        currentJob = {
          ...currentJob,
          jobId: data.jobId,
          inputPath: data.inputPath,
          status: data.status,
          result: data.result,
          error: data.error,
          startTime: data.startTime,
          endTime: data.endTime,
          promise: null, // Cannot restore promise
        };
        console.log('[HairgenJobTracker] Restored from storage:', data.status, data.jobId);
      }
    }
  } catch (e) {
    console.warn('[HairgenJobTracker] Failed to load from storage:', e);
  }
}

/**
 * Save job state to session storage (excludes promise).
 */
function saveToStorage() {
  try {
    const { promise, ...persistable } = currentJob;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
  } catch (e) {
    console.warn('[HairgenJobTracker] Failed to save to storage:', e);
  }
}

// ========================================
// JOB ID GENERATION
// ========================================

/**
 * Generate a unique job ID based on timestamp and input path.
 * This ensures each upload gets a unique identifier.
 */
function generateJobId(inputPath) {
  const timestamp = Date.now();
  const pathHash = inputPath ? inputPath.slice(-20) : 'unknown';
  return `hairgen_${timestamp}_${pathHash}`;
}

// ========================================
// PUBLIC API
// ========================================

/**
 * Start a new hairgen job. Automatically invalidates any previous job.
 * 
 * @param {string} inputPath - Firebase storage path of the input image
 * @param {function} triggerFn - Async function that calls the Firebase function
 * @returns {string} The generated jobId
 * 
 * @example
 * const jobId = startHairgenJob(storagePath, () => triggerHairGeneration({ fileName: storagePath }));
 */
export function startHairgenJob(inputPath, triggerFn) {
  if (!inputPath || typeof triggerFn !== 'function') {
    console.error('[HairgenJobTracker] Invalid arguments to startHairgenJob');
    return null;
  }

  const jobId = generateJobId(inputPath);
  
  console.log('[HairgenJobTracker] Starting new job:', jobId, 'for:', inputPath);
  
  // Reset state for new job
  currentJob = {
    jobId,
    inputPath,
    status: 'pending',
    result: null,
    error: null,
    startTime: Date.now(),
    endTime: null,
    promise: null,
  };
  
  // Save pending state
  saveToStorage();
  
  // Execute the trigger function and track the promise
  const promise = triggerFn();
  currentJob.promise = promise;
  
  // Attach completion handlers
  promise
    .then((result) => {
      // Only update if this is still the current job (handles race conditions)
      if (currentJob.jobId === jobId) {
        console.log('[HairgenJobTracker] Job completed successfully:', jobId);
        currentJob.status = 'success';
        currentJob.result = result;
        currentJob.endTime = Date.now();
        currentJob.promise = null;
        saveToStorage();
      } else {
        console.log('[HairgenJobTracker] Job completed but was superseded:', jobId);
      }
    })
    .catch((error) => {
      // Only update if this is still the current job
      if (currentJob.jobId === jobId) {
        console.error('[HairgenJobTracker] Job failed:', jobId, error);
        currentJob.status = 'error';
        currentJob.error = error?.message || String(error) || 'Unknown error';
        currentJob.endTime = Date.now();
        currentJob.promise = null;
        saveToStorage();
      } else {
        console.log('[HairgenJobTracker] Job failed but was superseded:', jobId);
      }
    });
  
  return jobId;
}

/**
 * Get current job status and data.
 * 
 * @returns {Object} Current job state
 * - jobId: string | null
 * - inputPath: string | null
 * - status: 'idle' | 'pending' | 'success' | 'error'
 * - result: API result object | null
 * - error: string | null
 * - startTime: number | null (ms timestamp)
 * - endTime: number | null (ms timestamp)
 * - promise: Promise | null (only available if pending)
 */
export function getHairgenJobStatus() {
  return { ...currentJob };
}

/**
 * Wait for the current job to complete (if pending).
 * Returns immediately if job is already complete or idle.
 * 
 * @returns {Promise<Object>} Resolves with job status when complete
 */
export async function waitForHairgenJob() {
  if (currentJob.status === 'pending' && currentJob.promise) {
    console.log('[HairgenJobTracker] Waiting for pending job:', currentJob.jobId);
    try {
      await currentJob.promise;
    } catch (e) {
      // Error already handled in startHairgenJob
    }
  }
  return { ...currentJob };
}

/**
 * Check if a given inputPath matches the current job.
 * Used to verify the job result is for the expected image.
 * 
 * @param {string} inputPath - Firebase storage path to check
 * @returns {boolean} True if matches current job
 */
export function isJobForInput(inputPath) {
  return currentJob.inputPath === inputPath;
}

/**
 * Check if the current job is for a specific jobId.
 * Used for race condition prevention.
 * 
 * @param {string} jobId - Job ID to check
 * @returns {boolean} True if matches current job
 */
export function isCurrentJob(jobId) {
  return currentJob.jobId === jobId;
}

/**
 * Clear the current job and reset to idle state.
 * Called when user starts completely fresh.
 */
export function clearHairgenJob() {
  console.log('[HairgenJobTracker] Clearing job state');
  currentJob = {
    jobId: null,
    inputPath: null,
    status: 'idle',
    result: null,
    error: null,
    startTime: null,
    endTime: null,
    promise: null,
  };
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Get elapsed time since job started (in ms).
 * Returns null if no job or job not started.
 * 
 * @returns {number | null} Elapsed milliseconds
 */
export function getJobElapsedTime() {
  if (!currentJob.startTime) return null;
  const endTime = currentJob.endTime || Date.now();
  return endTime - currentJob.startTime;
}

// ========================================
// INITIALIZATION
// ========================================

// Load any persisted state on module init
loadFromStorage();

console.log('[HairgenJobTracker] Module initialized, status:', currentJob.status);
